import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Table, Modal, Form, Input, Typography, App, Breadcrumb, Tooltip, Space, theme, Card, Row, Col } from "antd";
import { useAuth } from "../../context/AuthProvider";
import ForbiddenPage from "../Status/ForbiddenPage";
import { EditOutlined, RocketOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { getBackendUrl } from '../../utils/env';
import axios from "axios";
const { Title, Text } = Typography;

export default function AdminActivividadesDashboard() {
  const { message } = App.useApp();
  const { user } = useAuth();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (user?.id === 1) {
      fetchActivities();
    }
  }, [user]);

  const fetchActivities = () => {
    axios.get(`${getBackendUrl()}api/admin/activities.php`)
      .then(res => {
        if (res.data.success) {
          setActivities(res.data.activities);
        } else {
          message.error("No se pudieron cargar las actividades");
        }
      })
      .catch(() => message.error("Error al cargar las actividades"));
  };
  const openModal = (activity = null) => {
    setEditingActivity(activity);
    form.setFieldsValue(activity || { name: "" });
    setModalVisible(true);
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();

      const url = editingActivity
        ? `${getBackendUrl()}api/admin/activities.php?action=update&id=${editingActivity.id}`
        : `${getBackendUrl()}api/admin/activities.php?action=create`;

      const response = await axios.post(url, values, {
        headers: { "Content-Type": "application/json" },
      });

      const data = response.data;

      if (data.success) {
        message.success(editingActivity ? "Actividad actualizada" : "Actividad creada");
        fetchActivities();
        setModalVisible(false);
        form.resetFields();
      } else {
        message.error(data.message || "Error al guardar actividad");
      }
    } catch (error) {
      message.error("Error al guardar actividad");
    }
  };
  const handleDeleteActivity = async (id) => {
    try {
      const response = await axios.delete(
        `${getBackendUrl()}api/admin/activities.php?action=delete&id=${id}`
      );

      const data = response.data;

      if (data.success) {
        message.success("Actividad eliminada");
        fetchActivities();
      } else {
        message.error("Error al eliminar");
      }
    } catch (error) {
      message.error("Error al eliminar");
    }
  };

const columns = [
    {
      title: "Nombre de la Actividad",
      dataIndex: "name",
      key: "name", // Mantiene el nombre visible al scrollear
      width: 200,
      render: (text) => (
        <Text strong style={{ color: token.colorTextHeading }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
       // Botones siempre visibles en el borde derecho
      width: 120,
      align: 'center',
      render: (record) => (
        <Space size="middle">
          <Tooltip title="Editar" color={token.colorPrimary}>
            <Button 
              type="text" 
              icon={<EditOutlined style={{ color: token.colorInfo }} />} 
              onClick={() => openModal(record)} 
            />
          </Tooltip>
          <Tooltip title="Eliminar" color={token.colorError}>
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => handleDeleteActivity(record.id)} 
            />
          </Tooltip>
        </Space>
      ),
    },
  ];



  if (user?.id !== 1) return <ForbiddenPage />;

  return (
   <div style={{ 
      padding: 'clamp(16px, 4vw, 32px)', 
      maxWidth: 1000, // Un poco más estrecho por tener pocas columnas
      margin: '0 auto',
      backgroundColor: token.colorBgLayout,
      minHeight: '100vh'
    }}>
      
      {/* NAVEGACIÓN */}
      <Breadcrumb
        style={{ marginBottom: 24 }}
        items={[
          { title: <span onClick={() => navigate("/admin")} style={{ cursor: "pointer", color: token.colorPrimary }}>Panel de Administración</span> },
          { title: "Gestión de Actividades" },
        ]}
      />

      {/* CABECERA */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space align="center" size="middle">
            <div style={{ 
              backgroundColor: token.colorInfoBg, 
              padding: 10, 
              borderRadius: token.borderRadius,
              display: 'flex'
            }}>
              <RocketOutlined style={{ fontSize: 20, color: token.colorInfo }} />
            </div>
            <div>
              <Title level={3} style={{ margin: 0 }}>Gestión de Actividades</Title>
              <Text type="secondary">Crea y organiza las dinámicas del sistema.</Text>
            </div>
          </Space>
        </Col>
        <Col xs={24} sm={'auto'} style={{ marginTop: token.marginXS }}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            size="large"
            onClick={() => openModal()}
            block // En móviles ocupa todo el ancho
          >
            Crear Actividad
          </Button>
        </Col>
      </Row>

      {/* TABLA DENTRO DE CARD */}
      <Card 
        style={{ 
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowTertiary,
          border: `1px solid ${token.colorBorderSecondary}`
        }}
        bodyStyle={{ padding: token.paddingSM }}
      >
        <Table
          columns={columns}
          dataSource={activities}
          rowKey="id"
          loading={loading}
          size="middle"
          // EL SECRETO PARA MÓVIL:
          scroll={{ x: 500 }} // Forzamos 500px para que en teléfonos de 300-400px haya scroll
          pagination={{
            pageSize: 10,
            hideOnSinglePage: true,
            showTotal: (total) => `Total: ${total} actividades`
          }}
        />
      </Card>

      {/* MODAL ADAPTADO AL TEMA */}
      <Modal
        title={
          <Text strong style={{ fontSize: 18 }}>
            {editingActivity ? <EditOutlined /> : <PlusOutlined />} 
            {editingActivity ? " Editar Actividad" : " Crear Actividad"}
          </Text>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleModalSubmit}
        okText="Guardar Cambios"
        cancelText="Cancelar"
        centered
      >
        <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item 
            label="Nombre de la Actividad" 
            name="name"
            allowClear 
            rules={[{ required: true, message: "El nombre es obligatorio" }]}
          >
            <Input placeholder="Ej: Dinámica de Integración" size="large" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
